import { getConfig } from '@/config';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import {
  appHttpService,
  authHttpService,
} from '@/services/standard/http-service';
import { LocalStorageService } from '@/services/storage/local-storage-service';
import {
  getLocationOrigin,
  getLocationSearch,
  setLocationHref,
  setLocationSearch,
} from '@/utils/location-utils';
import { models, type Nullish, utils } from 'cesg-shared';
import { ref } from 'vue';

export interface AuthState {
  me?: models.GetMeResponse;
  loading: boolean;
  refreshing?: boolean;
  verificationState?: string;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number | undefined;
}

const createAuthState = (): AuthState => ({
  me: undefined,
  loading: false,
  refreshing: false,
  verificationState: undefined,
  accessToken: undefined,
  refreshToken: undefined,
  expiresAt: undefined,
});

// exported for mocking
export class FortnoxAuthService extends LocalStorageService<AuthState> {
  private _authState = ref<AuthState>({
    me: undefined as models.GetMeResponse | undefined,
    loading: false,
    refreshing: false,
  });

  refreshPromise: Promise<void> | null = null;

  constructor() {
    super('auth', createAuthState);
    this.setState(this.getState());
  }

  login(): void {
    setLocationHref(this.authUrl());
  }

  logout(): void {
    this.setState(createAuthState());
  }

  async handleRedirect(): Promise<void> {
    this.setState({ loading: false });
    overlaySpinnerService.hide();

    const queriesString = getLocationSearch().slice(1);
    const queries = new URLSearchParams(queriesString);

    const authorizationCode = queries.get('code');
    const verificationState = queries.get('state');
    const { verificationState: savedVerificationState } = this.getState();

    if (authorizationCode && verificationState === savedVerificationState) {
      this.setState({ verificationState, loading: true });
      overlaySpinnerService.show();

      try {
        const response = await this.getAccessTokenFromAuthorizationCode({
          authorizationCode,
          redirectUri: this.redirectUri(),
        });

        const me = await this.getMe(response.accessToken);
        this.setState({
          me,
          loading: false,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          expiresAt: Date.now() + response.expiresIn * 1000,
        });
        overlaySpinnerService.hide();
        setLocationSearch('');
      } catch (error) {
        console.error('Failed to handle authentication redirect.', error);
        this.logout();
      }
    }
  }

  // was called from main.mountApp()
  async handleMeState(): Promise<void> {
    if (!this.accessToken) {
      return;
    }
  }

  isExpired(offsetMinutes: number = 0): boolean {
    const { expiresAt } = this.getState();
    if (!expiresAt) {
      return true;
    }

    return expiresAt - Date.now() < offsetMinutes * 60 * 1000;
  }

  async refreshAccessToken() {
    const { refreshToken } = this.getState();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    if (this.refreshPromise) return this.refreshPromise;

    this.setState({ refreshing: true });
    overlaySpinnerService.show();
    this.refreshPromise = this.getAccessTokenFromRefreshToken({
      refreshToken,
    })
      .then((response) => {
        this.setState({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          expiresAt: Date.now() + response.expiresIn * 1000,
        });
      })
      .catch((err) => {
        this.setState({
          accessToken: undefined,
          refreshToken: undefined,
          expiresAt: undefined,
        });
        throw err;
      })
      .finally(() => {
        this.refreshPromise = null;
        this.setState({
          loading: false,
          refreshing: false,
        });
        overlaySpinnerService.hide();
      });
    return this.refreshPromise;
  }

  getAccessTokenFromAuthorizationCode({
    authorizationCode,
    redirectUri,
  }: models.GetAccessTokenRequest): Promise<models.GetAccessTokenResponse> {
    return authHttpService.request
      .url('/auth/access-token')
      .post({
        authorizationCode,
        redirectUri,
      })
      .json<models.GetAccessTokenResponse>();
  }

  getAccessTokenFromRefreshToken({
    refreshToken,
  }: models.GetAccessTokenFromRefreshTokenRequest): Promise<models.GetAccessTokenResponse> {
    return authHttpService.request
      .url('/auth/refresh-token')
      .headers({
        fortnoxauthorization: `Bearer ${this.accessToken}`,
      })
      .post({
        refreshToken,
      })
      .json<models.GetAccessTokenResponse>();
  }

  /**
   * Visible for testing
   */
  getMe(accessToken?: string): Promise<models.GetMeResponse> {
    const me = appHttpService.request
      .url('/auth/me')
      .headers({
        fortnoxauthorization: `Bearer ${accessToken ?? this.accessToken}`,
      })
      .get()
      .json<models.GetMeResponse>();
    return me;
  }

  async getMeAndUpdateState(
    accessToken?: string,
  ): Promise<models.GetMeResponse> {
    const me = await this.getMe(accessToken);

    this.setState({
      me,
    });

    return me;
  }

  get accessToken(): Nullish<string> {
    return this.getState().accessToken;
  }

  get refreshToken(): Nullish<string> {
    return this.getState().refreshToken;
  }

  get refreshing(): boolean {
    return this.getState().refreshing === true;
  }

  get authenticated(): boolean {
    return Boolean(this.getState().refreshToken);
  }

  protected override setState(
    state?: Partial<AuthState> | undefined,
  ): AuthState {
    const newState = super.setState(state);
    this._authState.value = newState;
    return newState;
  }

  async getAccessToken() {
    const refresh = async () => {
      await this.refreshAccessToken();
      const token = this.accessToken;
      if (!token) throw new Error('No access token available');
      return token;
    };

    if (this.refreshPromise) {
      await this.refreshPromise;
      const token = this.accessToken;
      if (!token) throw new Error('No access token available');
      return token;
    }

    const token = this.accessToken;
    if (!token) {
      return refresh();
    }

    if (this.isExpired(5)) {
      return refresh();
    }

    return token;
  }

  get authState() {
    return this._authState.value;
  }

  private redirectUri() {
    return getLocationOrigin();
  }

  private authUrl(): string {
    const { verificationState } = this.setState({
      verificationState: utils.randomString(),
    });

    const config = getConfig();

    const queries = new URLSearchParams({
      access_type: 'offline',
      client_id: config.FORTNOX_CLIENT_ID,
      redirect_uri: this.redirectUri(),
      response_type: 'code',
      state: verificationState!,
      scope:
        'profile article bookkeeping invoice project supplier supplierinvoice connectfile archive inbox',
    });

    return `${config.FORTNOX_AUTH_URL}?${queries}`;
  }
}

export const fortnoxAuthService = new FortnoxAuthService();
